import { leadInPlainText } from "@/lib/lead-in-html"
import {
  SHOWCASE_PACEMAKER_PASSAGE,
  SHOWCASE_PACEMAKER_PASSAGE_ALT,
} from "@/lib/question-preview"
import type { AuthoringQuestionType } from "@/lib/library-authoring"

import {
  isApplyableQualityAlternate,
  sortQuestionQualityChecks,
} from "./confidence"
import { formatTypeAvailable } from "./format-ask"
import { leoTryDistractorInsteadChip } from "./leo-cta-labels"
import {
  ensureMinSpanRewrites,
  optionIdForSelection,
  replaceSelectedSpan,
  selectionCoversField,
  selectionEnhancementCheck,
  selectionSpanRewrites,
  selectionVariationFieldLabel,
  toSelectionPlain,
  type ComposerEnhanceSelection,
} from "./selection-ask"
import { visibleQuestionQualityChecks } from "./run-checks"
import {
  namedSetLabel,
  topicCompleteDistractorSets,
  topicDistractorBank,
} from "./topic-options"
import type {
  QuestionQualityAlternate,
  QuestionQualityCheck,
  QuestionQualitySnapshot,
} from "./types"

export const VARIATION_STEM_CHECK_ID = "variation-stem"
export const VARIATION_OPTIONS_CHECK_ID = "variation-options"
export const VARIATION_BOTH_CHECK_ID = "variation-both"
export const VARIATION_ENTIRE_SET_CHECK_ID = "variation-entire-set"
export const VARIATION_SET_CHECK_ID = "variation-set"
export const VARIATION_REFERENCES_CHECK_ID = "variation-references"
export const VARIATION_ASK = "Give me a variation"
export const VARIATION_BOTH_ASK = "Vary the stem and the options"
export const VARIATION_ENTIRE_SET_ASK = "Vary the entire set"
/** Holistic chip when stem and distractors still have open quality flags. */
export const VARIATION_ENTIRE_QUESTION_CHIP = "Rewrite stem and all distractors"
export const VARIATION_ONE_DISTRACTOR_ASK = "Vary one distractor"
export const VARIATION_FEW_DISTRACTORS_ASK = "Vary two distractors"
export const VARIATION_ALL_DISTRACTORS_ASK = "Vary all distractors"
/** Stem-only variation chip — rewrites the question sentence, not distractors. */
export const VARIATION_STEM_CHIP = "Rewrite the stem"
/** Distractor-only variation chip — swaps wrong answer choices, not the stem. */
export const VARIATION_DISTRACTORS_CHIP = "Swap distractors"

export type VariationTarget =
  | "stem"
  | "options"
  | "options-one"
  | "options-few"
  | "options-all"
  | "both"
  | "entire-set"
  | "all"
  | "set"
  | "references"

/** How many distractors a variation ask should return. */
export type VariationOptionScope = "default" | "one" | "few" | "all"

/** What to offer first when quality is quiet. */
export type VariationKind = "stem" | "options" | "both" | "set"

export type VariationCapabilities = {
  numeric: boolean
  fillBlank: boolean
  matchingPairs: boolean
  ordering: boolean
  hotspot: boolean
}

export function parseVariationOptionLetter(prompt: string): string | undefined {
  const match = prompt
    .trim()
    .match(/\b(?:option|distractor|choice)\s*([a-h])\b/i)
  return match?.[1]?.toUpperCase()
}

export function variationOptionScopeFromTarget(
  target: VariationTarget,
): VariationOptionScope {
  if (target === "options-one") return "one"
  if (target === "options-few") return "few"
  if (target === "options-all") return "all"
  return "default"
}

export function parseVariationAsk(prompt: string): VariationTarget | null {
  const text = prompt.trim().toLowerCase()
  if (!text) return null
  if (
    /^see a different variation$/.test(text) ||
    /^write an alternate form$/.test(text)
  ) {
    return "set"
  }
  if (
    /\b(reference|passage|pdf|attachment)\b/.test(text) &&
    /(change|different|another|vary|replace)/.test(text)
  ) {
    return "references"
  }
  if (
    /another form|alternate form|draft another form|parallel form/.test(text)
  ) {
    return "set"
  }
  if (
    /^rewrite stem and all distractors$/.test(text) ||
    /entire (set|question|item)|whole (question|item)|complete (rewrite|variation)|everything together|stem and all (options|distractors)/.test(
      text,
    )
  ) {
    return "entire-set"
  }
  if (
    /stem and (the )?(options|distractors)|vary the stem and|both the stem|stem plus (all )?(options|distractors)/.test(
      text,
    )
  ) {
    return "both"
  }
  if (
    /all (the )?(options|distractors|wrong)|every (option|distractor)|rewrite all (options|distractors)|replace all (options|distractors)/.test(
      text,
    )
  ) {
    return "options-all"
  }
  if (
    /(two|few|some|a couple of?) (options|distractors)|vary two (options|distractors)/.test(
      text,
    )
  ) {
    return "options-few"
  }
  if (
    /(one|single|a) (option|distractor)|this (option|distractor)|(?:option|distractor|choice)\s*[a-h]\b/.test(
      text,
    )
  ) {
    return "options-one"
  }
  if (
    /^swap distractors$|^try different distractors$/.test(text) ||
    /only (the )?(options|distractors)|just (the )?(options|distractors)|different options|vary the options|another set of (options|distractors)|different distractors|stronger (distractors|options)|make the distractors/.test(
      text,
    )
  ) {
    return "options"
  }
  if (
    /^rewrite the stem$|^try another stem$/.test(text) ||
    (/only (the )?stem|just (the )?stem|rewrite (this |the )?stem/.test(text) &&
      !/\binstead\b/.test(text))
  ) {
    return "stem"
  }
  const named =
    /\bvariations?\b/.test(text) ||
    /another version|different version/.test(text) ||
    /rephrase|reword|different sentence|another sentence/.test(text) ||
    /another wording|different wording/.test(text) ||
    /keep this format and enhance the wording/.test(text)
  if (!named) return null
  if (/\b(stem|sentence|wording)\b/.test(text) && !/\b(option|distractor)s?\b/.test(text)) {
    return "stem"
  }
  return "all"
}

export function snapshotHasOptions(snapshot: QuestionQualitySnapshot): boolean {
  return (
    (snapshot.type === "mcq_single" || snapshot.type === "mcq_multiple") &&
    snapshot.options.length >= 2
  )
}

export function variationFollowUps(
  snapshot: QuestionQualitySnapshot,
  capabilities: VariationCapabilities,
  options: { hasWording: boolean; hasOptions?: boolean },
): string[] {
  const rows: string[] = []
  if (options.hasWording) rows.push(VARIATION_STEM_CHIP)
  const offerOptions = options.hasOptions ?? snapshotHasOptions(snapshot)
  if (offerOptions) rows.push(VARIATION_DISTRACTORS_CHIP)
  if (snapshot.hasReferences) rows.push("Give me a different passage")
  const current = snapshot.type
  const candidates: Array<{ type: AuthoringQuestionType; label: string }> = [
    { type: "fill_blank", label: "Change to Fill in the blank" },
    { type: "mcq_multiple", label: "Change to Multi-select" },
    { type: "mcq_single", label: "Change to Single choice" },
  ]
  for (const row of candidates) {
    if (row.type === current) continue
    if (!formatTypeAvailable(row.type, capabilities)) continue
    rows.push(row.label)
    if (rows.length >= 4) break
  }
  return rows
}

/** Variation chips after the quality trail — stem and distractors only. */
export function postQualityVariationFollowUps(
  snapshot: QuestionQualitySnapshot,
): string[] {
  const rows: string[] = []
  if (snapshotHasStem(snapshot)) rows.push(VARIATION_STEM_CHIP)
  if (snapshotHasOptions(snapshot)) rows.push(VARIATION_DISTRACTORS_CHIP)
  return rows
}

/** Ask Leo resting state: always offer one or two next-step chips after apply. */
export function leoHeroRestingFollowUps(
  snapshot: QuestionQualitySnapshot,
  capabilities: VariationCapabilities = {},
  max = 2,
): string[] {
  const rows: string[] = []
  const push = (label: string) => {
    if (rows.length >= max || !label || rows.includes(label)) return
    rows.push(label)
  }

  for (const label of postQualityVariationFollowUps(snapshot)) {
    push(label)
  }
  if (rows.length >= max) return rows

  for (const label of variationFollowUps(snapshot, capabilities, {
    hasWording: snapshotHasStem(snapshot),
  })) {
    push(label)
    if (rows.length >= max) break
  }
  if (rows.length >= max) return rows

  push(VARIATION_ASK)
  push(VARIATION_STEM_CHIP)
  return rows.slice(0, max)
}

export function isVariationCheckId(id: string): boolean {
  return (
    id === VARIATION_STEM_CHECK_ID ||
    id === VARIATION_OPTIONS_CHECK_ID ||
    id === VARIATION_BOTH_CHECK_ID ||
    id === VARIATION_ENTIRE_SET_CHECK_ID ||
    id === VARIATION_SET_CHECK_ID ||
    id === VARIATION_REFERENCES_CHECK_ID ||
    id === "selection-enhance"
  )
}

export function snapshotHasStem(snapshot: QuestionQualitySnapshot): boolean {
  return toSelectionPlain(snapshot.leadIn).length > 0
}

/**
 * Best variation to show when there is no quality defect.
 * Usage evidence → entire set. Otherwise stem, options, or both.
 */
export function variationKindFor(
  snapshot: QuestionQualitySnapshot,
  options: { offerSet?: boolean } = {},
): VariationKind {
  if (options.offerSet) return "set"
  const stem = snapshotHasStem(snapshot)
  const optionRows = snapshotHasOptions(snapshot)
  if (stem && optionRows) return "both"
  if (optionRows) return "options"
  return "stem"
}

/** Follow-ups after a canvas selection when quality is quiet — intent first. */
export function selectionVariationFollowUps(
  target: VariationTarget,
  snapshot: QuestionQualitySnapshot,
  capabilities: VariationCapabilities,
): string[] {
  const stem = snapshotHasStem(snapshot)
  const optionRows = snapshotHasOptions(snapshot)
  const rows: string[] = []
  const push = (row: string) => {
    if (!rows.some((item) => item.toLowerCase() === row.toLowerCase())) {
      rows.push(row)
    }
  }

  if (target === "stem") {
    push(VARIATION_STEM_CHIP)
    if (optionRows) {
      push(VARIATION_ONE_DISTRACTOR_ASK)
      push(VARIATION_ALL_DISTRACTORS_ASK)
      push(VARIATION_BOTH_ASK)
      push(VARIATION_ENTIRE_SET_ASK)
    }
  } else if (
    target === "options-one" ||
    target === "options-few" ||
    target === "options-all" ||
    target === "options"
  ) {
    push(VARIATION_ONE_DISTRACTOR_ASK)
    if (optionRows) {
      push(VARIATION_FEW_DISTRACTORS_ASK)
      push(VARIATION_ALL_DISTRACTORS_ASK)
    }
    if (stem) push(VARIATION_STEM_CHIP)
    if (stem && optionRows) {
      push(VARIATION_BOTH_ASK)
      push(VARIATION_ENTIRE_SET_ASK)
    }
  } else if (target === "both" || target === "entire-set") {
    push(VARIATION_BOTH_ASK)
    push(VARIATION_ENTIRE_SET_ASK)
  }

  const blocked = new Set(rows.map((row) => row.toLowerCase()))
  const extras = variationFollowUps(snapshot, capabilities, {
    hasWording: stem && target !== "stem",
    hasOptions: optionRows && !target.startsWith("options"),
  }).filter((row) => !blocked.has(row.toLowerCase()))
  return [...rows, ...extras].slice(0, 5)
}

const MIN_SELECTION_VARIATIONS = 2

function mergeVariationAlternates(
  ...groups: Array<QuestionQualityAlternate[] | undefined>
): QuestionQualityAlternate[] {
  const out: QuestionQualityAlternate[] = []
  const seen = new Set<string>()
  for (const group of groups) {
    for (const item of group ?? []) {
      if (!isApplyableQualityAlternate(item)) continue
      const key = item.text.trim().toLowerCase()
      if (!key || seen.has(key)) continue
      seen.add(key)
      out.push(item)
    }
  }
  return out
}

function normalizeOptionLabel(value: string): string {
  return toSelectionPlain(value).replace(/^the\s+/i, "").trim().toLowerCase()
}

function selectionOptionWordingAlternate(
  snapshot: QuestionQualitySnapshot,
  selection: ComposerEnhanceSelection,
): QuestionQualityAlternate | null {
  if (selection.field !== "option") return null
  const optionId = optionIdForSelection(selection, snapshot.options)
  if (!optionId) return null
  const option = snapshot.options.find((row) => row.id === optionId)
  if (!option) return null
  const source = option.text
  const span = toSelectionPlain(selection.text)
  const rewrite = ensureMinSpanRewrites(span, selectionSpanRewrites(span), 1)[0]
  if (!rewrite) return null
  const text = replaceSelectedSpan(source, span, rewrite)
  return {
    id: `selection-option-wording-${optionId}`,
    text,
    actionKind: "apply-text",
    actionLabel: "Use this distractor",
    patch: {
      optionTexts: [{ id: optionId, text, from: source.trim() }],
    },
    parameterDeltas: OPTION_VARIATION_DELTAS,
    clearsCheckIds: [VARIATION_OPTIONS_CHECK_ID],
  }
}

/** Topic-bank swaps — different plausible distractors, not more phrasing of the same word. */
function selectionOptionBankAlternates(
  snapshot: QuestionQualitySnapshot,
  selection: ComposerEnhanceSelection,
  wording?: QuestionQualityAlternate | null,
): QuestionQualityAlternate[] {
  if (selection.field !== "option") return []
  const optionId = optionIdForSelection(selection, snapshot.options)
  if (!optionId) return []
  const option = snapshot.options.find((row) => row.id === optionId)
  if (!option) return []
  const currentKey = normalizeOptionLabel(option.text)
  const wordingKey = wording?.text
    ? normalizeOptionLabel(wording.text)
    : normalizeOptionLabel(selection.text)
  const usedOnOtherOptions = new Set(
    snapshot.options
      .filter((row) => row.id !== optionId)
      .map((row) => normalizeOptionLabel(row.text)),
  )
  const bank = unusedOptionBank(snapshot, showcaseOptionBank(snapshot.leadIn))
  const alternates: QuestionQualityAlternate[] = []

  for (const [index, bankText] of bank.entries()) {
    const text = bankText.trim()
    const key = normalizeOptionLabel(text)
    if (!key || key === currentKey || key === wordingKey) continue
    if (usedOnOtherOptions.has(key)) continue
    if (
      conservativeOptionText(text).toLowerCase() ===
      conservativeOptionText(option.text).toLowerCase()
    ) {
      continue
    }
    alternates.push({
      id: `selection-option-bank-${optionId}-${index}`,
      text,
      actionKind: "apply-text",
      actionLabel: "Use this distractor",
      patch: {
        optionTexts: [{ id: optionId, text, from: option.text.trim() }],
      },
      parameterDeltas: OPTION_VARIATION_DELTAS,
      clearsCheckIds: [VARIATION_OPTIONS_CHECK_ID],
    })
  }
  return alternates
}

function selectionOptionExtraWordings(
  snapshot: QuestionQualitySnapshot,
  selection: ComposerEnhanceSelection,
): QuestionQualityAlternate[] {
  if (selection.field !== "option") return []
  const optionId = optionIdForSelection(selection, snapshot.options)
  const option = snapshot.options.find((row) => row.id === optionId)
  if (!optionId || !option) return []
  const span = toSelectionPlain(selection.text)
  return ensureMinSpanRewrites(span, selectionSpanRewrites(span))
    .slice(1)
    .map((rewrite, index) => {
      const text = replaceSelectedSpan(option.text, span, rewrite)
      return {
        id: `selection-option-wording-${optionId}-${index + 1}`,
        text,
        actionKind: "apply-text" as const,
        actionLabel: "Use this distractor",
        patch: {
          optionTexts: [{ id: optionId, text, from: option.text.trim() }],
        },
        parameterDeltas: OPTION_VARIATION_DELTAS,
        clearsCheckIds: [VARIATION_OPTIONS_CHECK_ID],
      } satisfies QuestionQualityAlternate
    })
}

/** Full ranked list: wording, bank swaps, then extra wordings. */
export function selectionOptionAllVariationAlternates(
  snapshot: QuestionQualitySnapshot,
  selection: ComposerEnhanceSelection,
): QuestionQualityAlternate[] {
  const wording = selectionOptionWordingAlternate(snapshot, selection)
  const banks = selectionOptionBankAlternates(snapshot, selection, wording)
  const extraWordings = selectionOptionExtraWordings(snapshot, selection)
  return mergeVariationAlternates(
    wording ? [wording] : undefined,
    banks,
    extraWordings,
  )
}

/** Canvas selection: one wording + one topic-bank swap when possible. */
export function selectionOptionVariationAlternates(
  snapshot: QuestionQualitySnapshot,
  selection: ComposerEnhanceSelection,
): QuestionQualityAlternate[] {
  const all = selectionOptionAllVariationAlternates(snapshot, selection)
  if (all.length >= MIN_SELECTION_VARIATIONS) {
    return all.slice(0, MIN_SELECTION_VARIATIONS)
  }
  return all
}

export function hasOpenStemAndDistractorQuality(
  checks: QuestionQualityCheck[],
  dismissedIds: string[] = [],
): boolean {
  const open = visibleQuestionQualityChecks(checks, dismissedIds).filter(
    (check) => !isVariationCheckId(check.id),
  )
  const stem = open.some((check) => check.field === "leadIn")
  const distractor = open.some(
    (check) => check.field === "option" || check.field === "options",
  )
  return stem && distractor
}

/**
 * One intent chip after two distractor cards.
 * When stem and distractors still have open quality flags, prefer the holistic
 * rewrite over another single-option guess.
 */
export function selectionVariationNextIntentChips(
  snapshot: QuestionQualitySnapshot,
  selection: ComposerEnhanceSelection,
  shown: readonly QuestionQualityAlternate[],
  checks: QuestionQualityCheck[],
  dismissedIds: string[] = [],
): string[] {
  if (
    hasOpenStemAndDistractorQuality(checks, dismissedIds) &&
    entireSetVariationCheck(snapshot, checks)
  ) {
    return [VARIATION_ENTIRE_QUESTION_CHIP]
  }

  const shownKeys = new Set(
    shown.map((item) => normalizeOptionLabel(item.text)),
  )
  const nextDistractor = selectionOptionAllVariationAlternates(
    snapshot,
    selection,
  ).find((item) => !shownKeys.has(normalizeOptionLabel(item.text)))
  if (nextDistractor) {
    return [leoTryDistractorInsteadChip(nextDistractor.text)]
  }

  if (snapshotHasStem(snapshot) && entireSetVariationCheck(snapshot, checks)) {
    return [VARIATION_ENTIRE_SET_ASK]
  }

  return []
}

function selectionVariationShell(
  snapshot: QuestionQualitySnapshot,
  selection: ComposerEnhanceSelection,
  target: VariationTarget,
  alternates: QuestionQualityAlternate[],
): QuestionQualityCheck {
  const letter = selection.optionLetter
  const home = selectionVariationFieldLabel(selection)
  const optionId = optionIdForSelection(selection, snapshot.options)
  if (target === "stem" || selection.field === "leadIn") {
    return {
      id: VARIATION_STEM_CHECK_ID,
      parameter: "clarity",
      severity: "caution",
      field: "leadIn",
      title: "Another wording for this stem selection",
      mechanism:
        "Use a suggestion on the canvas, or pick an action below.",
      currentText: toSelectionPlain(snapshot.leadIn),
      rubricConfidence: "medium",
      actionKind: "apply-text",
      actionLabel: alternates[0]?.actionLabel ?? "Use this stem",
      patch: alternates[0]?.patch,
      alternates,
    }
  }
  return {
    id: VARIATION_OPTIONS_CHECK_ID,
    parameter: "distractor",
    severity: "caution",
    field: "option",
    optionId,
    title: letter
      ? `Another wording for distractor ${letter}`
      : "Another wording for this distractor",
    mechanism: `Variation for ${home}. Use a suggestion on the canvas, or pick an action below.`,
    currentText: selection.text,
    rubricConfidence: "medium",
    actionKind: "apply-text",
    actionLabel: alternates[0]?.actionLabel ?? "Use this distractor",
    patch: alternates[0]?.patch,
    alternates,
  }
}

export function variationCheckForSelection(
  snapshot: QuestionQualitySnapshot,
  checks: QuestionQualityCheck[],
  selection: ComposerEnhanceSelection,
  target: VariationTarget,
): QuestionQualityCheck | null {
  const letter = selection.optionLetter
  const scoped = variationCheckForTarget(target, snapshot, checks, {
    prompt: letter ? `Option ${letter}` : undefined,
  })
  const span = selectionEnhancementCheck(snapshot, selection)
  const partial = span && !selectionCoversField(snapshot, selection)

  if (target === "stem") {
    if (partial && span) {
      const alternates = mergeVariationAlternates(
        span.alternates?.map((item) => ({
          ...item,
          clearsCheckIds: [VARIATION_STEM_CHECK_ID, ...item.clearsCheckIds],
        })),
      )
      if (alternates.length >= MIN_SELECTION_VARIATIONS) {
        return { ...span, id: VARIATION_STEM_CHECK_ID, alternates }
      }
    }
    const stemScoped = scoped ?? stemVariationCheck(snapshot)
    if (!stemScoped) return null
    const alternates = mergeVariationAlternates(stemScoped.alternates)
    return alternates.length > 0
      ? { ...stemScoped, alternates }
      : stemScoped
  }

  if (
    target === "options-one" ||
    target === "options-few" ||
    target === "options-all" ||
    target === "options"
  ) {
    if (partial && span) {
      const alternates = mergeVariationAlternates(
        span.alternates?.map((item) => ({
          ...item,
          actionLabel:
            selection.field === "option"
              ? "Use this distractor"
              : item.actionLabel,
          clearsCheckIds: [VARIATION_OPTIONS_CHECK_ID, ...item.clearsCheckIds],
        })),
      )
      if (alternates.length >= MIN_SELECTION_VARIATIONS) {
        return selectionVariationShell(snapshot, selection, target, alternates)
      }
    }
    const selectionAlternates = selectionOptionVariationAlternates(
      snapshot,
      selection,
    )
    if (selectionAlternates.length >= MIN_SELECTION_VARIATIONS) {
      return selectionVariationShell(
        snapshot,
        selection,
        target,
        selectionAlternates,
      )
    }
    const alternates = mergeVariationAlternates(
      selectionAlternates,
      scoped?.alternates,
    )
    if (alternates.length === 0) return scoped
    return selectionVariationShell(snapshot, selection, target, alternates)
  }

  return scoped
}

export function variationPossibilityFollowUps(
  kind: VariationKind,
  snapshot: QuestionQualitySnapshot,
  capabilities: VariationCapabilities,
): string[] {
  const rows: string[] = []
  const stem = snapshotHasStem(snapshot)
  const optionRows = snapshotHasOptions(snapshot)
  if (kind !== "stem" && kind !== "both" && stem) {
    rows.push(VARIATION_STEM_CHIP)
  }
  if (kind !== "options" && kind !== "both" && optionRows) {
    rows.push(VARIATION_DISTRACTORS_CHIP)
    rows.push(VARIATION_ONE_DISTRACTOR_ASK)
    rows.push(VARIATION_FEW_DISTRACTORS_ASK)
    rows.push(VARIATION_ALL_DISTRACTORS_ASK)
  }
  if (kind !== "both" && stem && optionRows) {
    rows.push(VARIATION_BOTH_ASK)
    rows.push(VARIATION_ENTIRE_SET_ASK)
  }
  if (kind !== "set") rows.push("Draft another form of this item")
  const blocked = new Set(rows)
  if (kind === "both") {
    blocked.add(VARIATION_STEM_CHIP)
    blocked.add(VARIATION_DISTRACTORS_CHIP)
    blocked.add(VARIATION_ONE_DISTRACTOR_ASK)
    blocked.add(VARIATION_FEW_DISTRACTORS_ASK)
    blocked.add(VARIATION_ALL_DISTRACTORS_ASK)
  }
  const extras = variationFollowUps(snapshot, capabilities, {
    hasWording: stem,
    hasOptions: optionRows,
  }).filter((row) => !blocked.has(row))
  return [...rows, ...extras].slice(0, 2)
}

function showcaseStemAlternates(
  snapshot: QuestionQualitySnapshot,
): QuestionQualityAlternate[] {
  if (!/amino acid increases net negative charge/i.test(snapshot.leadIn)) {
    return []
  }
  const leadIn =
    "At physiologic pH, which amino acid adds the most net negative charge to a protein?"
  return [
    {
      id: "variation-stem-showcase-q2",
      text: leadIn,
      actionKind: "apply-text",
      actionLabel: "Use this wording",
      patch: { leadIn },
      parameterDeltas: [
        { parameter: "clarity", effect: "clears" },
        { parameter: "cognitive", effect: "locked" },
      ],
      clearsCheckIds: [VARIATION_STEM_CHECK_ID],
    },
  ]
}

export function stemVariationCheck(
  snapshot: QuestionQualitySnapshot,
): QuestionQualityCheck | null {
  const text = toSelectionPlain(snapshot.leadIn)
  if (!text) return null
  const check = selectionEnhancementCheck(snapshot, { field: "leadIn", text })
  if (!check) return null
  const showcase = showcaseStemAlternates(snapshot)
  const alternates = [...showcase, ...(check.alternates ?? [])]
  return {
    ...check,
    id: VARIATION_STEM_CHECK_ID,
    title: "Another wording for Question",
    mechanism: "Use a suggestion on the canvas, or refine it here with Leo.",
    alternates: alternates.map((item) => ({
      ...item,
      clearsCheckIds: [VARIATION_STEM_CHECK_ID],
    })),
  }
}

function catalogOptionAlternates(
  checks: QuestionQualityCheck[],
): QuestionQualityAlternate[] {
  const optionChecks = sortQuestionQualityChecks(
    checks.filter(
      (check) => check.field === "option" || check.field === "options",
    ),
  )
  for (const check of optionChecks) {
    const seen = new Set<string>()
    const alternates: QuestionQualityAlternate[] = []
    for (const item of check.alternates ?? []) {
      if (!isApplyableQualityAlternate(item)) continue
      if (
        item.actionKind !== "apply-text" ||
        (!item.patch?.optionText && !item.patch?.optionTexts?.length)
      ) {
        continue
      }
      const key = item.text.trim().toLowerCase()
      if (!key || seen.has(key)) continue
      seen.add(key)
      const count = item.patch?.optionTexts?.length ?? 1
      alternates.push({
        ...item,
        id: `variation-option-catalog-${alternates.length}`,
        actionLabel:
          count > 2
            ? "Use all distractors"
            : count > 1
              ? "Use these distractors"
              : "Use this distractor",
        clearsCheckIds: [VARIATION_OPTIONS_CHECK_ID, ...item.clearsCheckIds],
      })
      if (alternates.length >= 4) break
    }
    if (alternates.length > 0) return alternates
  }
  return []
}

function optionIdFromLetter(
  snapshot: QuestionQualitySnapshot,
  letter: string,
): string | undefined {
  const index = letter.trim().toUpperCase().charCodeAt(0) - 65
  if (index < 0 || index >= snapshot.options.length) return undefined
  return snapshot.options[index]?.id
}

function optionAlternateScope(alternate: QuestionQualityAlternate): VariationOptionScope {
  if (alternate.id.startsWith("variation-option-one-")) return "one"
  if (
    alternate.id === "variation-option-few" ||
    alternate.id === "variation-option-many"
  ) {
    return "few"
  }
  if (alternate.id === "variation-option-all") return "all"
  const count = alternate.patch?.optionTexts?.length ?? 0
  if (count <= 1) return "one"
  if (count === 2) return "few"
  return "all"
}

function filterOptionAlternates(
  alternates: QuestionQualityAlternate[],
  scope: VariationOptionScope,
  snapshot: QuestionQualitySnapshot,
  optionLetter?: string,
): QuestionQualityAlternate[] {
  if (optionLetter) {
    const optionId = optionIdFromLetter(snapshot, optionLetter)
    if (!optionId) return []
    return alternates.filter((alternate) => {
      const rows = alternate.patch?.optionTexts
      if (rows?.length === 1 && rows[0]?.id === optionId) return true
      return alternate.id === `variation-option-one-${optionId}`
    })
  }
  if (scope === "default") return alternates
  const distractorCount = snapshot.options.filter(
    (option) => !option.isCorrect && option.text.trim(),
  ).length
  return alternates.filter((alternate) => {
    const rowCount = alternate.patch?.optionTexts?.length ?? 0
    const altScope = optionAlternateScope(alternate)
    if (scope === "one") return altScope === "one" || rowCount === 1
    if (scope === "few") return altScope === "few" || rowCount === 2
    if (scope === "all") {
      return altScope === "all" || rowCount >= Math.max(2, distractorCount)
    }
    return true
  })
}

export function optionVariationCheck(
  snapshot: QuestionQualitySnapshot,
  checks: QuestionQualityCheck[],
  scope: VariationOptionScope = "default",
  optionLetter?: string,
): QuestionQualityCheck | null {
  if (!snapshotHasOptions(snapshot)) return null
  const catalog = catalogOptionAlternates(checks)
  const clean = buildCleanOptionAlternates(snapshot)
  const merged = [...catalog, ...clean]
  const alternates = filterOptionAlternates(
    merged,
    scope,
    snapshot,
    optionLetter,
  )
  if (alternates.length === 0) return null
  const firstDistractor = snapshot.options.find(
    (option) => !option.isCorrect && option.text.trim(),
  )
  return {
    id: VARIATION_OPTIONS_CHECK_ID,
    parameter: "distractor",
    severity: "caution",
    field: "options",
    optionId: firstDistractor?.id,
    title: optionVariationTitle(scope, optionLetter),
    mechanism: "Use a suggestion on the canvas, or refine it here with Leo.",
    currentText: firstDistractor?.text,
    rubricConfidence: "medium",
    actionKind: "apply-text",
    actionLabel: alternates[0]?.actionLabel ?? "Use this distractor",
    patch: alternates[0]?.patch,
    alternates: alternates.map((item) => ({
      ...item,
      clearsCheckIds: [VARIATION_OPTIONS_CHECK_ID, ...item.clearsCheckIds],
    })),
  }
}

function optionVariationTitle(
  scope: VariationOptionScope,
  optionLetter?: string,
): string {
  if (optionLetter) return `Another distractor for Option ${optionLetter}`
  if (scope === "one") return "Another distractor"
  if (scope === "few") return "Another pair of distractors"
  if (scope === "all") return "Another full set of distractors"
  return "Another set of distractors"
}

const OPTION_VARIATION_DELTAS = [
  { parameter: "clarity" as const, effect: "clears" as const },
  { parameter: "cognitive" as const, effect: "locked" as const },
]

function showcaseOptionBank(stem: string): string[] {
  return topicDistractorBank(stem)
}

function unusedOptionBank(
  snapshot: QuestionQualitySnapshot,
  bank: string[],
): string[] {
  const used = new Set(
    snapshot.options.map((option) => option.text.trim().toLowerCase()),
  )
  return bank.filter((text) => !used.has(text.trim().toLowerCase()))
}

function conservativeOptionText(text: string): string {
  const trimmed = text.trim()
  if (/^the\s/i.test(trimmed)) {
    return trimmed.replace(/^the\s+/i, "").replace(/^\w/, (ch) => ch.toUpperCase())
  }
  return `The ${trimmed.charAt(0).toLowerCase()}${trimmed.slice(1)}`
}

function pickOptionRewrite(
  bank: string[],
  index: number,
  option: { text: string },
): string {
  return bank[index] ?? conservativeOptionText(option.text)
}

function buildCleanOptionAlternates(
  snapshot: QuestionQualitySnapshot,
): QuestionQualityAlternate[] {
  const distractors = snapshot.options.filter(
    (option) => !option.isCorrect && option.text.trim(),
  )
  if (distractors.length === 0) return []
  const used = snapshot.options.map((option) => option.text)
  const keyedUsed = snapshot.options
    .filter((option) => option.isCorrect)
    .map((option) => option.text)
  const bank = unusedOptionBank(snapshot, showcaseOptionBank(snapshot.leadIn))
  const alternates: QuestionQualityAlternate[] = []
  const seen = new Set<string>()

  const push = (alternate: QuestionQualityAlternate) => {
    const key = alternate.text.trim().toLowerCase()
    if (!key || seen.has(key)) return
    seen.add(key)
    alternates.push(alternate)
  }

  const allRows = distractors.map((option, index) => ({
    id: option.id,
    text: pickOptionRewrite(bank, index, option),
    from: option.text.trim(),
  }))
  push({
    id: "variation-option-all",
    text: namedSetLabel(allRows.map((row) => row.text)),
    actionKind: "apply-text",
    actionLabel:
      allRows.length === 1 ? "Use this distractor" : "Use all distractors",
    patch: { optionTexts: allRows },
    parameterDeltas: OPTION_VARIATION_DELTAS,
    clearsCheckIds: [VARIATION_OPTIONS_CHECK_ID],
  })

  if (distractors.length >= 2) {
    const fewRows = distractors.slice(0, 2).map((option, index) => ({
      id: option.id,
      text: pickOptionRewrite(bank, index, option),
      from: option.text.trim(),
    }))
    push({
      id: "variation-option-few",
      text: fewRows.map((row) => row.text).join("; "),
      actionKind: "apply-text",
      actionLabel: "Use these distractors",
      patch: { optionTexts: fewRows },
      parameterDeltas: OPTION_VARIATION_DELTAS,
      clearsCheckIds: [VARIATION_OPTIONS_CHECK_ID],
    })
  }

  for (const [index, option] of distractors.entries()) {
    const text = pickOptionRewrite(bank, index, option)
    push({
      id: `variation-option-one-${option.id}`,
      text,
      actionKind: "apply-text",
      actionLabel: "Use this distractor",
      patch: {
        optionTexts: [{ id: option.id, text, from: option.text.trim() }],
      },
      parameterDeltas: OPTION_VARIATION_DELTAS,
      clearsCheckIds: [VARIATION_OPTIONS_CHECK_ID],
    })
  }

  for (const [setIndex, names] of topicCompleteDistractorSets(
    snapshot.leadIn,
    keyedUsed,
    distractors.length,
  ).entries()) {
    const rows = distractors.map((option, index) => ({
      id: option.id,
      text: names[index] ?? pickOptionRewrite(bank, index + setIndex, option),
      from: option.text.trim(),
    }))
    push({
      id: `variation-option-pack-${setIndex}`,
      text: namedSetLabel(names),
      actionKind: "apply-text",
      actionLabel: "Use all distractors",
      patch: { optionTexts: rows },
      parameterDeltas: OPTION_VARIATION_DELTAS,
      clearsCheckIds: [VARIATION_OPTIONS_CHECK_ID],
    })
  }

  return alternates
}

export function referenceVariationCheck(
  snapshot: QuestionQualitySnapshot,
): QuestionQualityCheck | null {
  if (!snapshot.hasReferences) return null
  const alternates: QuestionQualityAlternate[] = [
    {
      id: "variation-passage-alt",
      text: "Shorter pacemaker passage",
      actionKind: "apply-text",
      actionLabel: "Use this passage",
      patch: { referencesHtml: SHOWCASE_PACEMAKER_PASSAGE_ALT },
      parameterDeltas: [],
      clearsCheckIds: [VARIATION_REFERENCES_CHECK_ID],
    },
    {
      id: "variation-passage-pdf",
      text: "Pacemaker-current.pdf",
      actionKind: "apply-text",
      actionLabel: "Use this file",
      patch: { referenceFileName: "Pacemaker-current.pdf" },
      parameterDeltas: [],
      clearsCheckIds: [VARIATION_REFERENCES_CHECK_ID],
    },
  ]
  return {
    id: VARIATION_REFERENCES_CHECK_ID,
    parameter: "clarity",
    severity: "caution",
    field: "references",
    title: "Another supporting material",
    mechanism: "Use this writes it in References.",
    currentText: SHOWCASE_PACEMAKER_PASSAGE.replace(/<[^>]+>/g, " ").trim(),
    rubricConfidence: "medium",
    actionKind: "apply-text",
    actionLabel: "Use this passage",
    patch: alternates[0]?.patch,
    alternates,
  }
}

export function bothVariationCheck(
  snapshot: QuestionQualitySnapshot,
  checks: QuestionQualityCheck[],
): QuestionQualityCheck | null {
  const stem = stemVariationCheck(snapshot)
  const options = optionVariationCheck(snapshot, checks, "default")
  if (!stem && !options) return null
  const alternates = [
    ...(stem?.alternates ?? []),
    ...(options?.alternates ?? []),
  ].filter((item): item is QuestionQualityAlternate => Boolean(item))
  if (alternates.length === 0) return null
  return {
    id: VARIATION_BOTH_CHECK_ID,
    parameter: "clarity",
    severity: "caution",
    field: "leadIn",
    title: "Another stem and distractors",
    mechanism:
      "Each card is one apply. Pick a stem or distractor set, then refine here.",
    currentText: stem?.currentText,
    rubricConfidence: "medium",
    actionKind: "apply-text",
    actionLabel: alternates[0]?.actionLabel ?? "Use this wording",
    patch: alternates[0]?.patch,
    alternates: alternates.map((item) => ({
      ...item,
      clearsCheckIds: [VARIATION_BOTH_CHECK_ID, ...item.clearsCheckIds],
    })),
  }
}

export function entireSetVariationCheck(
  snapshot: QuestionQualitySnapshot,
  checks: QuestionQualityCheck[],
): QuestionQualityCheck | null {
  const stem = stemVariationCheck(snapshot)
  const stemAlt =
    stem?.alternates?.find((item) => item.id === "variation-stem-showcase-q2") ??
    stem?.alternates?.[0]
  if (!stemAlt?.patch?.leadIn) return null
  const options = optionVariationCheck(snapshot, checks, "all")
  const optionAlt =
    options?.alternates?.find((item) => item.id === "variation-option-pack-0") ??
    options?.alternates?.find((item) => item.id === "variation-option-all") ??
    options?.alternates?.[0]
  if (!optionAlt?.patch?.optionTexts?.length) return null
  const alternates: QuestionQualityAlternate[] = [
    {
      id: "variation-entire-set-0",
      text: leadInPlainText(stemAlt.patch.leadIn),
      actionKind: "apply-text",
      actionLabel: "Use this set",
      patch: {
        leadIn: stemAlt.patch.leadIn,
        optionTexts: optionAlt.patch.optionTexts,
      },
      parameterDeltas: [
        { parameter: "clarity", effect: "clears" },
        { parameter: "cognitive", effect: "locked" },
      ],
      clearsCheckIds: [VARIATION_ENTIRE_SET_CHECK_ID],
    },
  ]
  return {
    id: VARIATION_ENTIRE_SET_CHECK_ID,
    parameter: "clarity",
    severity: "caution",
    field: "leadIn",
    title: "Entire set",
    mechanism:
      "Stem and all distractors together. Use this set, or refine it here with Leo.",
    currentText: stem?.currentText,
    rubricConfidence: "medium",
    actionKind: "apply-text",
    actionLabel: "Use this set",
    patch: alternates[0]?.patch,
    alternates,
  }
}

export function variationCheckForTarget(
  target: VariationTarget,
  snapshot: QuestionQualitySnapshot,
  checks: QuestionQualityCheck[],
  options: { setReason?: string; prompt?: string } = {},
): QuestionQualityCheck | null {
  const letter = options.prompt
    ? parseVariationOptionLetter(options.prompt)
    : undefined
  if (target === "set") return setVariationCheck(options.setReason)
  if (target === "entire-set") return entireSetVariationCheck(snapshot, checks)
  if (target === "both") return bothVariationCheck(snapshot, checks)
  if (
    target === "options" ||
    target === "options-one" ||
    target === "options-few" ||
    target === "options-all"
  ) {
    return optionVariationCheck(
      snapshot,
      checks,
      variationOptionScopeFromTarget(target),
      letter,
    )
  }
  if (target === "references") return referenceVariationCheck(snapshot)
  if (target === "stem") return stemVariationCheck(snapshot)
  return null
}

/** Ask Leo to refine a variation suggestion without applying it. */
export function variationModifyAsk(
  snapshot: QuestionQualitySnapshot,
  check: QuestionQualityCheck,
  alternate: QuestionQualityAlternate,
): string | null {
  if (
    alternate.actionKind === "open-leo" ||
    alternate.actionKind === "none" ||
    !alternate.patch
  ) {
    return null
  }
  const patch = alternate.patch
  if (patch.leadIn && patch.optionTexts?.length) {
    const stem = leadInPlainText(patch.leadIn)
    const options = patch.optionTexts.map((row) => row.text).join("; ")
    return `Refine this entire set. Question: "${stem}". Distractors: ${options}.`
  }
  if (patch.leadIn) {
    return `Enhance this selection on Question: "${leadInPlainText(patch.leadIn)}"`
  }
  if (patch.optionTexts?.length === 1) {
    const row = patch.optionTexts[0]
    const index = snapshot.options.findIndex((option) => option.id === row.id)
    const letter =
      index >= 0 ? String.fromCharCode(65 + index) : check.optionId ?? "?"
    return `Enhance this selection on Option ${letter}: "${row.text}"`
  }
  if (patch.optionTexts?.length) {
    return `Refine these distractors: ${patch.optionTexts.map((row) => row.text).join("; ")}`
  }
  if (patch.referencesHtml) {
    return "Refine this passage. Keep the same clinical point."
  }
  if (patch.referenceFileName) {
    return `Refine the reference file name "${patch.referenceFileName}".`
  }
  return `Refine this suggestion: "${alternate.text}"`
}

export function setVariationCheck(reason?: string): QuestionQualityCheck {
  const mechanism =
    reason ?? "Keep the same blueprint cell. Write a sibling item."
  return {
    id: VARIATION_SET_CHECK_ID,
    parameter: "exposure",
    severity: "caution",
    field: "leadIn",
    title: "Draft another form",
    mechanism,
    rubricConfidence: "medium",
    actionKind: "open-leo",
    actionLabel: "Write an alternate form",
    alternates: [
      {
        id: "variation-set-ask",
        text: "Keep the same blueprint cell. Write a sibling item.",
        actionKind: "open-leo",
        actionLabel: "Write an alternate form",
        parameterDeltas: [],
        clearsCheckIds: [VARIATION_SET_CHECK_ID],
      },
    ],
  }
}

export function variationCheckForKind(
  kind: VariationKind,
  snapshot: QuestionQualitySnapshot,
  checks: QuestionQualityCheck[],
  setReason?: string,
): QuestionQualityCheck | null {
  if (kind === "set") return setVariationCheck(setReason)
  if (kind === "both") return bothVariationCheck(snapshot, checks)
  if (kind === "options") return optionVariationCheck(snapshot, checks)
  return stemVariationCheck(snapshot) ?? setVariationCheck(setReason)
}
